import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import HeaderPage from "../../components/HeaderPage";
import NavBar from "../../components/NavBar";
import getDecryptData from "../../../helper/getDecryptData";
import eraseCookie from "../../../helper/eraseCookie";
import { authActions } from "../../../redux/store";

const DeleteAccount = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (!confirmDelete) {
      toast.error("Please confirm that you understand account deletion is permanent.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete your account? This action cannot be undone."
    );

    if (!confirmed) {
      return;
    }

    const decryptdatajson = getDecryptData();
    const id = decryptdatajson?._id;

    if (!id) {
      toast.error("User not found. Please login again.");
      navigate("/login");
      return;
    }

    setIsDeleting(true);

    try {
      await axiosInstance.put(`/admin/update-user/${id}`, { status: "0" });

      dispatch(authActions.logout());
      eraseCookie("token");
      eraseCookie("user");
      eraseCookie("userId");
      toast.success("Account deletion request submitted successfully.");
      navigate("/login");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error(error.response?.data?.message || "Unable to delete account. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <HeaderPage title={"Delete Account"} />
      <main className="page-content bg-light mypadding overflow-auto pb-4 px-0">
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              <div className="card shadow border-0 rounded-4">
                <div className="card-body p-4">
                  <div className="text-center mb-4">
                    <div
                      className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{ width: 80, height: 80 }}
                    >
                      <i className="bi bi-exclamation-triangle-fill text-danger fs-1"></i>
                    </div>

                    <h2 className="mt-3 fw-bold">Delete Account</h2>

                    <p className="text-muted mb-0">
                      Deleting your account is permanent. This action cannot be undone.
                    </p>
                  </div>

                  <div className="alert alert-danger">
                    <h6 className="fw-bold mb-3">Before deleting your account:</h6>

                    <ul className="mb-0">
                      <li>Your profile will be permanently deleted.</li>
                      <li>All saved data will be removed.</li>
                      <li>Your purchase history cannot be recovered.</li>
                      <li>You will lose access to all app services.</li>
                    </ul>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Reason for deleting (Optional)
                    </label>

                    <select
                      className="form-select"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                    >
                      <option value="">Select a reason</option>
                      <option value="Privacy concerns">Privacy concerns</option>
                      <option value="Found another app">Found another app</option>
                      <option value="Too many notifications">Too many notifications</option>
                      <option value="Not satisfied with the service">
                        Not satisfied with the service
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-semibold">Feedback (Optional)</label>

                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="Tell us how we can improve..."
                      value={feedback}
                      onChange={(event) => setFeedback(event.target.value)}
                    ></textarea>
                  </div>

                  <div className="form-check mb-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="confirmDelete"
                      checked={confirmDelete}
                      onChange={(event) => setConfirmDelete(event.target.checked)}
                    />

                    <label className="form-check-label" htmlFor="confirmDelete">
                      I understand that deleting my account is permanent and cannot be undone.
                    </label>
                  </div>

                  <div className="d-grid gap-2">
                    <button
                      className="btn btn-danger btn-lg"
                      type="button"
                      disabled={isDeleting}
                      onClick={handleDeleteAccount}
                    >
                      {isDeleting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-trash-fill me-2"></i>
                          Delete My Account
                        </>
                      )}
                    </button>

                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => navigate("/account")}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <NavBar />
      </main>
    </>
  );
};

export default DeleteAccount;
